// Dependencies
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Components
import { SpecialTag } from './index';

// Styled Components
import {
  MenuAnchor,
  MenuCaret,
  MenuPanel,
  MenuRow,
  MenuRowIconCircle,
  MenuRowVisibilityIcon,
  MenuRowEditIcon,
  MenuRowUnlinkIcon,
  MenuRowLinkIcon,
} from './styles';

// Types
import { tagActionsMenuProps } from './types';
import { tagTextColor } from './textColor';

// Icons (mismo asset que `ButtonIconBackground` variant `edit`)
import Pencil from '../../../assets/icons/pencil.svg';

/**
 * P15 opción C (2026-07-10, feedback usuario) — pill de vínculo con acciones
 * TÁCTILES. Sustituye a los mini-iconos inline de 16px (`TagActionIcon`):
 * demasiado pequeños para tablet (mínimo táctil ~44px) y dos targets
 * adyacentes propensos a error de dedo.
 *
 * Comportamiento:
 * - 1 acción  → la pill ENTERA es el target (tap = ejecuta). Sin caret ni
 *   menú: máxima superficie, cero pasos extra.
 * - 2+ acciones → la pill despliega un menú anclado con una fila por acción
 *   (min-height 4.4rem). El panel replica los tokens del popover canónico
 *   (`DropDownUserMenu.Container`); la pill NO crece — el caret ocupa el
 *   slot `actions` donde antes vivían los mini-iconos.
 *
 * Cierre: click-away (mousedown fuera del anchor) y tecla Escape.
 */
export const TagActionsMenu = ({
  variant,
  sizeTag = 'default',
  title,
  actions,
  testId,
}: tagActionsMenuProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const isSingleAction = actions.length === 1;

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleTagClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isSingleAction) {
        actions[0].onClick();
        return;
      }
      setOpen((prev) => !prev);
    },
    [isSingleAction, actions],
  );

  return (
    <MenuAnchor ref={anchorRef}>
      <SpecialTag
        variant={variant}
        sizeTag={sizeTag}
        title={title}
        onClick={handleTagClick}
        style={{ cursor: 'pointer' }}
        role="button"
        data-testid={testId}
        // El nombre accesible es el propio texto visible del tag (title);
        // no se sobreescribe con la acción — "Ver"/"Editar" a secas era
        // ambiguo con varias pills en pantalla.
        aria-haspopup={isSingleAction ? undefined : 'menu'}
        aria-expanded={isSingleAction ? undefined : open}
        actions={
          isSingleAction ? undefined : (
            <MenuCaret $open={open} $fill={tagTextColor(variant)} />
          )
        }
      />
      {open && (
        <MenuPanel role="menu">
          {actions.map((action) => (
            <MenuRow
              key={action.label}
              type="button"
              role="menuitem"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                action.onClick();
              }}
            >
              <MenuRowIconCircle>
                {action.icon === 'view' && <MenuRowVisibilityIcon />}
                {action.icon === 'edit' && <MenuRowEditIcon src={Pencil} alt="" />}
                {action.icon === 'unlink' && <MenuRowUnlinkIcon />}
                {action.icon === 'link' && <MenuRowLinkIcon />}
              </MenuRowIconCircle>
              {action.label}
            </MenuRow>
          ))}
        </MenuPanel>
      )}
    </MenuAnchor>
  );
};

export default TagActionsMenu;
